import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Input, Card, Alert, Loader, Badge, Checkbox } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';

const STATUS_VARIANT = {
  queued: 'warning',
  running: 'info',
  done: 'success',
  error: 'error',
  cancelled: 'default',
};

// Poll while anything is in flight. A crawl runs for minutes, so a websocket would be
// nicer, but a 3s poll on a handful of rows costs nothing and needs no new infrastructure.
const POLL_MS = 3000;

/** " (3 failed)" or "" — kept out of the JSX so the counts stay readable. */
const failedSuffix = (n) => (n ? ` (${n} failed)` : '');

export default function Scraper() {
  const { fetchApi } = useAppApi();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [url, setUrl] = useState('');
  const [maxPages, setMaxPages] = useState('50');
  const [maxDepth, setMaxDepth] = useState('5');
  const [desktop, setDesktop] = useState(true);
  const [mobile, setMobile] = useState(true);

  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await fetchApi('crud', {
        model: 'ScrapeJob', action: 'list', limit: 50, orderBy: { column: 'id', direction: 'desc' },
      });
      setJobs(Array.isArray(res) ? res : (res && res.data) || []);
      setErr('');
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setLoading(false);
    }
  }, [fetchApi]);

  useEffect(() => { load(); }, [load]);

  // Only poll while something is actually moving — an idle list shouldn't hit the API forever.
  useEffect(() => {
    const busy = jobs.some((j) => j.status === 'queued' || j.status === 'running');
    clearTimeout(timer.current);
    if (busy) timer.current = setTimeout(load, POLL_MS);
    return () => clearTimeout(timer.current);
  }, [jobs, load]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setSubmitting(true);
    const viewports = [desktop && 'desktop', mobile && 'mobile'].filter(Boolean);
    try {
      await fetchApi('crud', {
        model: 'ScrapeJob',
        action: 'create',
        data: {
          url: url.trim(),
          maxPages: Number(maxPages),
          maxDepth: Number(maxDepth),
          viewports,
          screenshots: viewports.length > 0,
        },
      });
      setUrl('');
      await load();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      key: 'id',
      label: 'Job',
      render: (row) => <Link className="text-primary-600 font-medium hover:underline" to={`/scraper/${row.id}`}>#{row.id}</Link>,
    },
    {
      key: 'url',
      label: 'URL',
      render: (row) => <span className="break-all">{row.url}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <Badge variant={STATUS_VARIANT[row.status] || 'default'}>{row.status}</Badge>,
    },
    {
      key: 'pages_done',
      label: 'Pages',
      render: (row) => `${row.pages_done}/${row.pages_found}${failedSuffix(row.pages_failed)}`,
    },
    {
      key: 'shots_ok',
      label: 'Shots',
      render: (row) => `${row.shots_ok}${failedSuffix(row.shots_failed)}`,
    },
    { key: 'created_at', label: 'Created' },
  ];

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold">Web Scraper</h1>

      <Can module="scraper" action="create">
        <Card className="p-4">
          <form onSubmit={submit} className="space-y-4">
            <Input
              label="URL to crawl"
              placeholder="example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
            {/* Free-typed numbers, not Selects. twreact's Select hands onChange the RAW
                VALUE, not a DOM event (and null when cleared), so the usual `e.target.value`
                handler throws and the field silently never updates — which is exactly how
                these two fields were broken. Input's onChange does pass a real event.
                No `max` here on purpose: the server is the authority and clamps
                (models/ScrapeJob.model.js), so a browser-side ceiling would only be a
                second place to keep in sync. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Max pages"
                type="number"
                min="1"
                step="1"
                value={maxPages}
                onChange={(e) => setMaxPages(e.target.value)}
              />
              <Input
                label="Max depth"
                type="number"
                min="0"
                step="1"
                value={maxDepth}
                onChange={(e) => setMaxDepth(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-6">
              <Checkbox label="Desktop screenshots" checked={desktop} onChange={(e) => setDesktop(e?.target ? e.target.checked : Boolean(e))} />
              <Checkbox label="Mobile screenshots" checked={mobile} onChange={(e) => setMobile(e?.target ? e.target.checked : Boolean(e))} />
            </div>
            <p className="text-sm opacity-70">
              Crawls the same site only, honours robots.txt, and screenshots each page.
              Pages are capped at 1000 and depth at 20; anything higher is clamped.
              Screenshots cost roughly 8s per page, so a 1000-page crawl with both
              viewports runs for over two hours.
            </p>
            <Button type="submit" loading={submitting} disabled={!url.trim()}>Start crawl</Button>
          </form>
        </Card>
      </Can>

      {err ? <Alert variant="error" title="Could not start the crawl">{err}</Alert> : null}

      {loading ? <Loader text="Loading crawls…" /> : <Table data={jobs} columns={columns} pagination globalSearch withAction={false} />}
    </div>
  );
}
